import { useState, useEffect, useRef } from "react";
import { useAuth }  from "@context/AuthContext";
import axios        from "axios";
import { API_BASE, FIRM_IMG_BASE } from "@config/env";

const post = (ep, fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k,v]) => { if (v != null) fd.append(k, v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};

// ── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position:"fixed", top:20, right:20, zIndex:9999, display:"flex", flexDirection:"column", gap:8, pointerEvents:"none" }}>
      {toasts.map(t => (
        <div key={t.id} style={{ padding:"12px 16px", borderRadius:10, boxShadow:"0 4px 20px rgba(0,0,0,0.12)", display:"flex", alignItems:"center", gap:10, minWidth:260, maxWidth:380, pointerEvents:"all", animation:"fc-toast-in 0.25s ease",
          background: t.type==="success" ? "var(--fc-green-light)" : t.type==="error" ? "var(--fc-red-light)" : "var(--fc-primary-light)",
          border:     t.type==="success" ? "1px solid var(--fc-green-border)" : t.type==="error" ? "1px solid var(--fc-red-border)" : "1px solid var(--fc-primary-border)",
          color:      t.type==="success" ? "var(--fc-green-text)" : t.type==="error" ? "#DC2626" : "#2563EB",
        }}>
          <i className={`fa-solid ${t.type==="success"?"fa-circle-check":t.type==="error"?"fa-circle-exclamation":"fa-info-circle"}`} style={{fontSize:16,flexShrink:0}} />
          <span style={{ fontSize:13, fontWeight:500, flex:1 }}>{t.message}</span>
        </div>
      ))}
      <style>{`@keyframes fc-toast-in{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}`}</style>
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (message, type="success", duration=3500) => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  };
  return { toasts, success: m => add(m,"success"), error: m => add(m,"error"), info: m => add(m,"info") };
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function EditFirmPage() {
  const { userSlug } = useAuth();
  const toast = useToast();

  const [loading,     setLoading]     = useState(true);
  const [firmDetails, setFirmDetails] = useState(null);
  const [firmName,    setFirmName]    = useState("");
  const [firmNameErr, setFirmNameErr] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBusy,    setLogoBusy]    = useState(false);
  const [busy,        setBusy]        = useState(false);
  const [touched,     setTouched]     = useState(false);

  useEffect(() => {
    if (!userSlug) { setLoading(false); return; }
    post("get-user-firm-details", { user_slug: userSlug }).then(data => {
      const d = data?.result?.firmDetails || data?.firmDetails || data?.data?.firmDetails || data?.result;
      if (d) {
        setFirmDetails(d);
        setFirmName(d.firm_name || "");
        setLogoPreview(d.firm_logo ? FIRM_IMG_BASE + d.firm_logo : null);
      }
      setLoading(false);
    });
  }, [userSlug]);

  const validateName = (val) => {
    if (!val.trim())           return "Firm name is required";
    if (val.trim().length < 2) return "Firm name must be at least 2 characters";
    if (val.trim().length > 100) return "Firm name must be less than 100 characters";
    return "";
  };

  const handleNameChange = (e) => {
    const v = e.target.value;
    setFirmName(v);
    if (touched) setFirmNameErr(validateName(v));
  };

  const handleNameBlur = () => {
    setTouched(true);
    setFirmNameErr(validateName(firmName));
  };

  const handleSave = async () => {
    setTouched(true);
    const err = validateName(firmName);
    setFirmNameErr(err);
    if (err) return;

    setBusy(true);
    const res = await post("update-firm-basic-info", {
      user_slug: userSlug,
      firm_name: firmName.trim(),
      firm_id:   firmDetails?.firm_id,
    });
    setBusy(false);

    if (Number(res?.status) === 1) {
      toast.success(res.message || "Firm details updated successfully!");
    } else {
      toast.error(res?.message || "Update failed. Please try again.");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }

    setLogoPreview(URL.createObjectURL(file));
    setLogoBusy(true);

    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("firm_id",   firmDetails?.firm_id || "");
    fd.append("file",      file);
    const res = await axios.post(`${API_BASE}firm-logo-upload`, fd).then(r => r.data).catch(() => null);
    setLogoBusy(false);

    if (Number(res?.status) === 1) {
      toast.success("Logo updated successfully!");
      if (res.firm_logo) setLogoPreview(FIRM_IMG_BASE + res.firm_logo);
    } else {
      toast.error(res?.message || "Logo upload failed. Please try again.");
      setLogoPreview(firmDetails?.firm_logo ? FIRM_IMG_BASE + firmDetails.firm_logo : null);
    }
  };

  return (
    <div>
      <Toast toasts={toast.toasts} />

      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:800, color:"var(--fc-text-primary)", margin:0 }}>Edit Firm Details</h1>
        <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"3px 0 0" }}>Update your organization's information</p>
      </div>

      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:28, boxShadow:"0 1px 4px rgba(0,0,0,0.04)", maxWidth:580 }}>
        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[1,2,3].map(i => <div key={i} style={{ height:44, background:"var(--fc-border-light)", borderRadius:9 }} />)}
          </div>
        ) : (
          <>
            {/* ── Logo ── */}
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>Firm Logo</div>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ width:80, height:80, borderRadius:14, background:"var(--fc-border-light)", border:`2px solid ${logoBusy?"#2563EB":"var(--fc-border)"}`, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0, transition:"border-color 0.2s" }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" style={{ width:"100%", height:"100%", objectFit:"contain", padding:4 }} />
                    : <i className="fa-solid fa-building" style={{ fontSize:24, color:"var(--fc-text-subtle)" }} />}
                </div>
                <div>
                  <label htmlFor="logo-upload">
                    <div className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill" style={{ display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer", opacity:logoBusy?0.6:1 }}>
                      {logoBusy
                        ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:10}}/> Uploading...</>
                        : <><i className="fa-solid fa-upload" style={{fontSize:10}}/> Upload Logo</>}
                    </div>
                  </label>
                  <input id="logo-upload" type="file" accept="image/*" disabled={logoBusy} onChange={handleLogoUpload} style={{ display:"none" }} />
                  <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:5 }}>PNG, JPG up to 5MB</div>
                </div>
              </div>
            </div>

            {/* ── Firm name ── */}
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                Firm Name <span style={{ color:"#DC2626" }}>*</span>
              </label>
              <input
                value={firmName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                placeholder="Enter firm name"
                style={{ width:"100%", boxSizing:"border-box", padding:"11px 12px", border:`1.5px solid ${firmNameErr?"#EF4444":touched&&!firmNameErr&&firmName?"#16A34A":"var(--fc-border)"}`, borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", transition:"all 0.18s" }}
                onFocus={e => { if (!firmNameErr) { e.target.style.borderColor="#2563EB"; e.target.style.boxShadow="0 0 0 3px rgba(37,99,235,0.1)"; }}}
                onBlurCapture={e => { e.target.style.boxShadow="none"; }}
              />
              {firmNameErr && (
                <div style={{ fontSize:11, color:"#DC2626", marginTop:5, display:"flex", alignItems:"center", gap:4 }}>
                  <i className="fa-solid fa-circle-exclamation" style={{fontSize:10}} />
                  {firmNameErr}
                </div>
              )}
              {touched && !firmNameErr && firmName && (
                <div style={{ fontSize:11, color:"#16A34A", marginTop:5, display:"flex", alignItems:"center", gap:4 }}>
                  <i className="fa-solid fa-circle-check" style={{fontSize:10}} />
                  Looks good!
                </div>
              )}
            </div>

            {/* ── Firm type (readonly) ── */}
            {firmDetails && (
              <div style={{ marginBottom:24 }}>
                <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>Firm Type</label>
                <div style={{ padding:"11px 12px", background:"var(--fc-bg-light)", border:"1.5px solid var(--fc-border)", borderRadius:10, fontSize:13, color:"var(--fc-text-muted)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span>{firmDetails.firm_type==="1"?"Staffing / Consulting Firm":firmDetails.firm_type==="2"?"Enterprise / End Client":"Independent Contractor"}</span>
                  <span style={{ fontSize:11, color:"var(--fc-text-subtle)", background:"var(--fc-border-light)", padding:"2px 8px", borderRadius:5 }}>read-only</span>
                </div>
              </div>
            )}

            {/* ── Save button ── */}
            <div style={{ display:"flex", justifyContent:"flex-end", paddingTop:4 }}>
              <button onClick={handleSave} disabled={busy || !!firmNameErr}
                style={{ minWidth:160, padding:"10px 24px", borderRadius:99, border:"none", background:busy||firmNameErr?"var(--fc-border)":"var(--fc-text-primary)", color:busy||firmNameErr?"var(--fc-text-subtle)":"var(--fc-card-bg)", fontSize:13, fontWeight:700, cursor:busy||firmNameErr?"not-allowed":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"all 0.2s" }}>
                {busy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}}/> Saving...</> : "Update Firm Details"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}